/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import getConfig from '../../config';
import https from 'https';
import {v4 as uuidv4} from 'uuid';
import {AppHomeSophiaRawBannerData, AppHomeSophiaBannerData} from '../containers/AppHome/types';

export const SophiaLocaleMap = {
	cs: 'cs_CZ',
	da: 'da_DK',
	de: 'de_DE',
	en: 'en_US',
	es: 'es_ES',
	fi: 'fi_FI',
	fr: 'fr_FR',
	it: 'it_IT',
	ja: 'ja_JP',
	ko: 'ko_KR',
	nb: 'nb_NO',
	nl: 'nl_NL',
	pl: 'pl_PL',
	pt: 'pt_BR',
	ru: 'ru_RU',
	sv: 'sv_SE',
	tr: 'tr_TR',
	'zh-Hans': 'zh_CN',
	'zh-Hant': 'zh_TW',
};

type SophiaHeaders = {
	'x-api-key': string;
	'Content-Type': string;
	Accept: string;
	'x-request-id': string;
};

type SophiaParams = {
	surfaceId: string;
	productLanguage: string;
};

class SophiaService {
	// Singleton
	static instance;

	headers: SophiaHeaders;

	constructor() {
		if (!SophiaService.instance) {
			SophiaService.instance = this;

			this.headers = {
				'x-api-key': getConfig('CLIENT_ID'),
				'Content-Type': 'application/json',
				Accept: 'application/json',
				'x-request-id': uuidv4(),
			};
		}
		return SophiaService.instance;
	}

	async fetchSurfaceDataFromSophia(surfaceId, locale, appContextType): Promise<AppHomeSophiaRawBannerData | Error> {
		// Get campaign data for surfaceId
		const params = {
			surfaceId,
			productLanguage: locale,
		};

		let surfaceData;
		// surfaceId=CCW_HOME_PS_BANNERS
		// surfaceId=CCW_HOME_CANVAS_BANNERS

		const response = await this._getSophiaCampaign(params);
		if (response instanceof Error) {
			surfaceData = response;
		}
		else {
			// Get the data for this surfaceId
			if (response && response.surfaces && Object.keys(response.surfaces).length !== 0) {
				//  Get the surface data
				surfaceData = response.surfaces[surfaceId];
				surfaceData.appContextType = appContextType;
			}
			else {
				surfaceData = new Error('Invalid Sophia data');
			}
		}
		return surfaceData;
	}

	/**
	 * Retrieves the Sophia campaign data given provided params for the surface and locale.
	 * NOTE: This resolves errors rather than rejects.
	 * @param {string} params optional query params for the request
	 * @returns {Object || Error}
	 */
	_getSophiaCampaign(params: SophiaParams): Promise<AppHomeSophiaBannerData | Error> {
		return new Promise((resolve, reject) => {
			const startTime = Date.now();
			const timeout = setTimeout(() => {
				const sophiaServiceError = new Error('Sophia Service timeout error');
				resolve(sophiaServiceError);
				clearTimeout(timeout);
			}, 1500);

			const options = {
				hostname: getConfig('SOPHIA_URI'),
				path: '/v2/content',
				headers: this.headers,
			};
			if (params) {
				const queryParams = new URLSearchParams();
				for (const [key, value] of Object.entries(params)) {
					queryParams.append(key, value);
				}
				options.path = `${options.path}?${queryParams.toString()}`;
			}

			const request = https.request(options, (res) => {
				clearTimeout(timeout);
				const statusCode = res.statusCode || 400; // for some reason, res.statusCode can be undefined
				if (statusCode >= 400) {
					const sophiaServiceError = new Error(`Sophia Service response error code: ${res.statusCode}`);
					resolve(sophiaServiceError);
				}
				else {
					let data = '';
					// set the encoding to UTF8 to prevent decoding issues on certain browsers.
					res.setEncoding('utf8');
					res.on('data', chunk => (data += chunk));
					res.on('end', () => {
						// Refer to the 'features' schema in CCHome App Server
						try {
							const resultJSON = JSON.parse(data);

							if (resultJSON) {
								window.cchome.util.logger.info(
									'Sophia Service',
									'Success fetching campaign data in Sophia Service.',
									{
										responseUrl: res.url,
										responseStatusCode: res.statusCode,
										responseStatusMessage: res.statusMessage,
										responseTime: Date.now() - startTime,
									},
								);
								resultJSON.url = res.url;
								resolve(resultJSON);
							}
							else {
								const sophiaServiceError = new Error(
									`Sophia Service response error code: ${res.statusCode}`,
								);
								resolve(sophiaServiceError);
							}
						}
						catch (e: any) {
							window.cchome.util.logger.info('Sophia Service', 'Sophia Service parsing error: ', {
								message: e && e.toString(),
								responseUrl: res.url,
								responseStatusCode: res.statusCode,
								responseStatusMessage: res.statusMessage,
								responseTime: Date.now() - startTime,
							});
							const sophiaServiceError = new Error('Sophia Service parsing error');
							resolve(sophiaServiceError);
						}
					});
				}
			});

			request.on('error', (err) => {
				clearTimeout(timeout);
				window.cchome.util.logger.info('Sophia Service', 'Sophia Service connect error', err.toString());
				const sophiaServiceError = new Error('Sophia Service connect error');
				resolve(sophiaServiceError);
			});

			request.end();
		});
	}
}

export default new SophiaService();
